import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import SearchAndFilter from "./SearchAndFilter";
import CustomerDialog from "./CustomerDialog";
import { useRestaurantData } from "./hooks/useRestaurantData";
import { useOrderManagement } from "./hooks/useOrderManagement";
import { useCustomerManagement } from "./hooks/useCustomerManagement";
import api from "../../../../../features/auth/axiosConfig";
import OrderSection from "./OrderSection/OrderSection";
import { usePaymentTheme } from "../respayment/hooks/usePaymentTheme";
import { PageTitle } from "src/shared/PageTitle";
import { useEditOrderHandler } from "./hooks/useEditOrderHandler";
import { useOrderModificationTracker } from "./hooks/useOrderModificationTracker";
import { useWindowManagement } from "./hooks/useWindowManagement";
import { useOrderOperations } from "./hooks/useOrderOperations";
import CategoryCarousel from "./components/CategoryCarousel";
import ProductDisplaySection from "./components/ProductDisplaySection";
import LoadingScreen from "src/shared/components/LoadingScreen";
import { ToastContainer } from "react-toastify";
import { useUser } from "src/business-types/grocery/components/UserContext";
import OpenCloseDayPopup from "src/shared/modules/admin/shift/OpenCloseDayPopup";
import { normalizeShiftTypes } from "src/shared/utils/normalizeShiftTypes";
import { useBusinessOperations } from "src/shared/modules/admin/shift/useBusinessOperations";
import SubscriptionDialog from "src/shared/modules/admin/auth/components/SubscriptionDialog";

const Reshome = () => {
  const headerRef = useRef<{ refreshOrders: () => void }>(null);
  const { orderid } = useParams<{ orderid: any }>();
  const { categories, products, loading } = useRestaurantData();
  const [selectedCategoryradio, setSelectedCategoryradio] = useState<string>("none");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [tableNumber, setTableNumber] = useState<string>("");
  const [guess, setGuess] = useState<string>("");
  const [newopen, setNewOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [isDelivery, setIsDelivery] = useState(false);
  const { subscriptionInfo } = useUser();
  const [showDialog, setShowDialog] = useState(false);
  const [message, setMessage] = useState<React.ReactNode>("");
  const [orderType, setOrderType] = useState<string>(isDelivery ? "Delivery" : "Table");

  const {
    selectedProducts,
    setSelectedProducts,
    discount,
    setDiscount,
    deliveryFee,
    setDeliveryFee,
    isEditing,
    setIsEditing,
    calculateSubtotal,
    calculateTotal,
    handleIncrement,
    handleDecrement,
    deleteProduct,
    handleNoteChange,
    editingNote,
    setEditingNote,
  } = useOrderManagement();

  const {
    customers,
    filteredCustomers,
    setFilteredCustomers,
    pagenumber,
    setPagenumber,
    total,
    customerName,
    setCustomerName,
    phoneNumber,
    setPhoneNumber,
    customerAdd,
    setCustomerAdd,
    setSearchTerm,
    searchTerm,
    fetchCustomerDetails,
  } = useCustomerManagement();

  const { oldProducts, setOldProducts } = useEditOrderHandler({
    products,
    setSelectedProducts,
    setDiscount,
    setIsEditing,
    setCustomerName,
    setPhoneNumber,
    setTableNumber,
    setGuess,
    setOrderType,
    setDeliveryFee,
  });
  const { isOrderModified } = useOrderModificationTracker({ selectedProducts, isEditing, oldProducts, discount, tableNumber, guess, orderType, deliveryFee });

  useWindowManagement();

  const { open, setOpen, handleCancelOrder, handleUpdateOrder, handleSubmit, resetFormFields } = useOrderOperations({
    selectedProducts,
    setSelectedProducts,
    customerName,
    setCustomerName,
    phoneNumber,
    setPhoneNumber,
    tableNumber,
    setTableNumber,
    guess,
    setGuess,
    discount,
    setDiscount,
    oldProducts,
    setOldProducts,
    isEditing,
    setIsEditing,
    calculateSubtotal,
    calculateTotal,
    isDelivery,
    headerRef,
    setCustomerAdd,
    setSelectedCategory,
    setSelectedCategoryradio,
    setSearchQuery,
    setCurrentPage,
    fetchCustomerDetails,
    deliveryFee,
    setDeliveryFee,
  });

  const { userConfigration } = useUser();
  const { refreshStatus } = useBusinessOperations();

  const toBoolean = (value: any) => value === true || value === "true";
  const deliveryFeeEnabled = toBoolean(userConfigration?.deliveryFeeEnabled);
  const [showOpenDayPopup, setShowOpenDayPopup] = useState(false);
  const [openableShifts, setOpenableShifts] = useState<string[]>([]);
  const [businessId, setBusinessId] = useState<string | null>(null);

  // ===================== OPEN DAY LOGIC =========================

  useEffect(() => {
    if (toBoolean(userConfigration.businessOperationEnable) === true) {
      api.get(`/app/v1/business_operations?status=OPEN`).then((res) => {
        const BusinessDay = res.data.result[0];
        console.log(BusinessDay);
        if (BusinessDay?.status === "OPEN") {
          setShowOpenDayPopup(false);
        } else {
          setShowOpenDayPopup(true);
        }
      });
    } else {
      setShowOpenDayPopup(false);
    }
  }, [userConfigration.businessOperationEnable]);

  const { fontClass } = usePaymentTheme();

  useEffect(() => {
    setIsDelivery(orderType === "Delivery");
  }, [orderType]);

  useEffect(() => {
    if (!subscriptionInfo) return;

    const alreadyShown = sessionStorage.getItem("subscription_dialog_shown");
    if (alreadyShown === "true") return;

    // GRACE PERIOD CASE
    if (subscriptionInfo.status === "grace_period") {
      const days = subscriptionInfo.daysRemaining;

      const formattedMessage = (
        <>
          This is a reminder that your current subscription will expire in <strong>{days} day(s)</strong>. Please renew now to avoid service disruption.
        </>
      );

      setMessage(formattedMessage);
      setShowDialog(true);
      sessionStorage.setItem("subscription_dialog_shown", "true");
      return;
    }

    // WARNING CASE
    if (subscriptionInfo.warning) {
      setMessage(subscriptionInfo.warning);
      setShowDialog(true);
      sessionStorage.setItem("subscription_dialog_shown", "true");
      return;
    }
  }, [subscriptionInfo]);

  // Keep your existing useEffect for orderid
  useEffect(() => {
    const fetchOrder = async (id: string) => {
      try {
        await api.get(`/app/v1/pos/order${id}`);
      } catch (err) {
        console.error(err);
      }
    };

    if (orderid) {
      fetchOrder(orderid);
    }
  }, [orderid]);

  // Reset to first page when categories change
  useEffect(() => {
    document.title = "Home › iMetanic";
    setCurrentPage(0);
  }, [categories]);

  const filteredProducts = useMemo(() => {
    if (!products?.length) return [];

    const search = searchQuery?.trim().toLowerCase();
    const type = selectedCategoryradio?.toLowerCase();

    return products.filter(({ Type, ProductName, category_id }) => {
      if (type && type !== "none" && Type?.toLowerCase() !== type) return false;
      if (search && !ProductName?.toLowerCase().includes(search)) return false;
      if (selectedCategory && category_id !== selectedCategory) return false;
      return true;
    });
  }, [products, selectedCategory, selectedCategoryradio, searchQuery]);

  const handleOpenDialog = () => {
    setNewOpen(true);
  };

  const handleCloseDialog = () => {
    setNewOpen(false);
  };

  const handleReload = () => {
    resetFormFields();
    handleCloseDialog();
  };

  const handleNoteClick = (productId: string) => {
    setEditingNote(productId);
  };

  const handleNoteBlur = (orderId: string) => {
    setEditingNote(null);
  };

  const handleCloseCustomer = () => {
    setCustomerAdd(false);
    setCustomerName("");
    setPhoneNumber("");
    setTableNumber("");
  };

  // Handle successful open day - with debounce to prevent multiple calls
  const handleOpenDaySuccess = useCallback(
    (newShiftId?: string) => {
      console.log("Open day success, newShiftId:", newShiftId);
      if (newShiftId) {
        localStorage.setItem("activeShiftId", newShiftId);
        setBusinessId(newShiftId);

        // Dispatch custom event to notify ProfileSection
        window.dispatchEvent(
          new CustomEvent("shiftStatusChanged", {
            detail: { status: "OPEN", shiftId: newShiftId },
          }),
        );

        // Also directly refresh status
        setTimeout(() => {
          refreshStatus();
        }, 100);
      }
      setShowOpenDayPopup(false);
    },
    [refreshStatus],
  );

  return (
    <div className={`flex flex-col h-[90.8vh] overflow-hidden bg-[#F9F8FA] ${fontClass}`}>
      <PageTitle title="- Point of Sale › Home" />
      <div className="thin-scrollbar w-full flex-1 flex flex-col lg:flex-row gap-1 p-1 h-[93vh] overflow-auto">
        <div className="flex flex-col lg:flex-row w-full h-full gap-2">
          {/* Left Panel */}
          <div className="w-full lg:w-2/3 h-full flex flex-col bg-[#F9F8FA] rounded-lg shadow-md">
            <SearchAndFilter
              selectedCategoryradio={selectedCategoryradio}
              setSelectedCategoryradio={setSelectedCategoryradio}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              handleOpenDialog={handleOpenDialog}
              newopen={newopen}
              handleCloseDialog={handleCloseDialog}
              handleReload={handleReload}
            />

            <CategoryCarousel categories={categories} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory} currentPage={currentPage} setCurrentPage={setCurrentPage} />

            <ProductDisplaySection
              key={`${selectedCategory}-${selectedCategoryradio}-${searchQuery}`}
              filteredProducts={filteredProducts}
              selectedProducts={selectedProducts}
              handleIncrement={handleIncrement}
              handleDecrement={handleDecrement}
            />
          </div>

          <OrderSection
            isOrderModified={isOrderModified}
            isDelivery={isDelivery}
            setIsDelivery={setIsDelivery}
            tableNumber={tableNumber}
            guess={guess}
            setGuess={setGuess}
            setTableNumber={setTableNumber}
            oldProducts={oldProducts}
            selectedProducts={selectedProducts}
            editingNote={editingNote}
            handleNoteChange={handleNoteChange}
            handleNoteClick={handleNoteClick}
            handleNoteBlur={handleNoteBlur}
            deleteProduct={deleteProduct}
            handleDecrement={handleDecrement}
            handleIncrement={handleIncrement}
            calculateSubtotal={calculateSubtotal}
            discount={discount}
            setDiscount={setDiscount}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            calculateTotal={calculateTotal}
            handleCancelOrder={handleCancelOrder}
            handleUpdatOrder={() => handleUpdateOrder(isOrderModified)}
            setOpen={setOpen}
            open={open}
            customers={customers}
            filteredCustomers={filteredCustomers}
            setFilteredCustomers={setFilteredCustomers}
            pagenumber={pagenumber}
            setPagenumber={setPagenumber}
            total={total}
            sendorder={handleSubmit}
            setCustomerAdd={setCustomerAdd}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            resetFormFields={resetFormFields}
            deliveryFee={deliveryFee}
            setDeliveryFee={setDeliveryFee}
            deliveryFeeEnabled={deliveryFeeEnabled}
          />
        </div>
        {loading && <LoadingScreen />}
      </div>

      <CustomerDialog
        customerAdd={customerAdd}
        handleCloseCustomer={handleCloseCustomer}
        customerName={customerName}
        setCustomerName={setCustomerName}
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        handleSubmit={handleSubmit}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
      />
      <SubscriptionDialog
        open={showDialog}
        message={message}
        onClose={() => setShowDialog(false)}
        onRenew={() => {
          setShowDialog(false);
          // navigate to subscription page
        }}
      />
      <ToastContainer />
      {showOpenDayPopup && <OpenCloseDayPopup isOpen={showOpenDayPopup} mode="open" shiftTypes={openableShifts} onClose={() => setShowOpenDayPopup(false)} onSuccess={handleOpenDaySuccess} />}
    </div>
  );
};

export default Reshome;
